// src/pages/InformeChoferes.jsx
import React, { useMemo, useState } from "react";
import {
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Spinner,
  Alert,
  Table,
} from "react-bootstrap";

import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

import { getInformeChoferes } from "../helpers/rutaReportes";

const fmtARS = (n) =>
  Number(n || 0).toLocaleString("es-AR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const todayYYYYMMDD = () => new Date().toISOString().slice(0, 10);

const sortES = (a, b) =>
  String(a || "").localeCompare(String(b || ""), "es", { sensitivity: "base" });

export default function InformeChoferes() {
  const [fecha, setFecha] = useState(todayYYYYMMDD());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const consultar = async () => {
    setErr("");
    setLoading(true);
    try {
      const r = await getInformeChoferes(fecha);
      if (!r?.ok) {
        throw new Error(r?.error || r?.msg || "No se pudo cargar el reporte");
      }
      setData(r);
    } catch (e) {
      setData(null);
      setErr(e?.message || "Error");
    } finally {
      setLoading(false);
    }
  };

  const exportarPDF = async () => {
    if (!data) return;

    const LOGO_URL = "/Danlu.png";
    const EMPRESA = "TRANSPORTE DANLU";
    const LINEA1 = "Dirección / Localidad / Provincia";
    const LINEA2 = "Tel: xxx  •  mail@dominio.com";
    const FOOTER_TXT = "Documento electrónico generado por el sistema DANLU";

    const safeFile = (s) =>
      String(s || "")
        .trim()
        .replace(/[^\w]+/g, "_")
        .replace(/^_+|_+$/g, "");

    const loadImageAsDataURL = async (url) => {
      try {
        const r = await fetch(url, { cache: "no-store" });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const blob = await r.blob();
        const mime = blob.type || "";
        const dataUrl = await new Promise((resolve, reject) => {
          const fr = new FileReader();
          fr.onload = () => resolve(fr.result);
          fr.onerror = reject;
          fr.readAsDataURL(blob);
        });
        return { dataUrl, mime };
      } catch {
        return { dataUrl: null, mime: "" };
      }
    };

    const { dataUrl: logoDataUrl, mime: logoMime } = await loadImageAsDataURL(LOGO_URL);

    const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();

    const M_LEFT = 10;
    const M_RIGHT = 10;
    const M_TOP = 8;
    const M_BOTTOM = 8;

    const HEADER_H = 30;
    const FOOTER_H = 10;

    const title = "Resumen de Deuda a Choferes";
    const subtitle = `Fecha de corte: ${data?.fechaCorte || fecha}`;

    const drawHeader = () => {
      const y0 = M_TOP + 2;

      if (logoDataUrl) {
        const LOGO_W = 18;
        const LOGO_H = 14;
        const format = (logoMime || "").includes("jpeg") ? "JPEG" : "PNG";
        try {
          doc.addImage(logoDataUrl, format, M_LEFT, y0, LOGO_W, LOGO_H);
        } catch {}
      }

      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.text(EMPRESA, pageW / 2, y0 + 6, { align: "center" });

      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.5);
      doc.text(LINEA1, pageW / 2, y0 + 11, { align: "center" });
      doc.text(LINEA2, pageW / 2, y0 + 15, { align: "center" });

      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.text(title, M_LEFT, y0 + 22);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.text(subtitle, M_LEFT, y0 + 27);

      doc.setDrawColor(180);
      doc.line(M_LEFT, M_TOP + HEADER_H, pageW - M_RIGHT, M_TOP + HEADER_H);
    };

    const drawFooter = (pageNumber, totalPages) => {
      const y = pageH - M_BOTTOM;

      doc.setDrawColor(200);
      doc.line(M_LEFT, y - FOOTER_H + 2, pageW - M_RIGHT, y - FOOTER_H + 2);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.text(FOOTER_TXT, M_LEFT, y - 2);
      doc.text(`Página ${pageNumber} de ${totalPages}`, pageW - M_RIGHT, y - 2, {
        align: "right",
      });
    };

    const tot = data?.totales || {};
    const drawTotales = (startY) => {
      let y = startY;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);

      doc.text(`Total Liquidado: $ ${fmtARS(tot.liquidado)}`, M_LEFT, y);
      y += 5;
      doc.text(`Total Pagado: $ ${fmtARS(tot.pagado)}`, M_LEFT, y);
      y += 5;
      doc.text(`Saldo Pendiente: $ ${fmtARS(tot.pendiente)}`, M_LEFT, y);

      return y + 4;
    };

    drawHeader();
    const yCursor = drawTotales(M_TOP + HEADER_H + 8);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text("Choferes", M_LEFT, yCursor + 4);

    autoTable(doc, {
      startY: yCursor + 7,
      head: [["Chofer", "Viajes", "Liquidado", "Pagado", "Saldo"]],
      body: choferes.map((x) => [
        x?.nombre || "",
        String(x?.viajes || 0),
        `$ ${fmtARS(x?.totalLiquidado)}`,
        `$ ${fmtARS(x?.totalPagado)}`,
        `$ ${fmtARS(x?.saldo)}`,
      ]),
      styles: { fontSize: 8, cellPadding: 1.4 },
      headStyles: { fontStyle: "bold" },
      columnStyles: {
        1: { halign: "center", cellWidth: 20 },
        2: { halign: "right", cellWidth: 35 },
        3: { halign: "right", cellWidth: 35 },
        4: { halign: "right", cellWidth: 35 },
      },
      margin: {
        left: M_LEFT,
        right: M_RIGHT,
        top: M_TOP + HEADER_H,
        bottom: M_BOTTOM + FOOTER_H,
      },
      didDrawPage: () => {
        drawHeader();
        drawTotales(M_TOP + HEADER_H + 8);
      },
    });

    const totalPages = doc.getNumberOfPages();
    for (let p = 1; p <= totalPages; p++) {
      doc.setPage(p);
      drawFooter(p, totalPages);
    }

    doc.save(`informe-choferes-${safeFile(data?.fechaCorte || fecha)}.pdf`);
  };

  const tot = data?.totales || {};

  const choferes = useMemo(() => {
    return (data?.choferes || [])
      .filter(
        (x) =>
          Number(x?.totalLiquidado || 0) !== 0 ||
          Number(x?.totalPagado || 0) !== 0 ||
          Number(x?.saldo || 0) !== 0
      )
      .sort((a, b) => {
        if (Number(b?.saldo || 0) !== Number(a?.saldo || 0)) {
          return Number(b?.saldo || 0) - Number(a?.saldo || 0);
        }
        return sortES(a?.nombre, b?.nombre);
      });
  }, [data]);

  return (
    <Container fluid="md" className="py-3">
      <Card className="shadow-sm border-0">
        <Card.Body>
          <div className="d-flex flex-column flex-md-row align-items-md-end justify-content-between gap-3">
            <div style={{ minWidth: 240 }}>
              <Form.Group>
                <Form.Label className="mb-1">Fecha de corte</Form.Label>
                <Form.Control
                  type="date"
                  value={fecha}
                  onChange={(e) => setFecha(e.target.value)}
                />
              </Form.Group>
            </div>

            <div className="d-flex gap-2">
              <Button onClick={consultar} disabled={loading}>
                {loading ? (
                  <>
                    <Spinner size="sm" /> Consultando…
                  </>
                ) : (
                  "Consultar"
                )}
              </Button>

              <Button
                variant="secondary"
                onClick={exportarPDF}
                disabled={!data || loading}
              >
                Exportar PDF
              </Button>
            </div>
          </div>

          {err ? (
            <Alert className="mt-3 mb-0" variant="danger">
              {err}
            </Alert>
          ) : null}
        </Card.Body>
      </Card>

      {!data ? (
        <div className="mt-3 text-muted">
          Elegí una fecha y tocá <b>“Consultar”</b>.
        </div>
      ) : (
        <>
          <Row className="mt-3 g-2">
            <Col md={4}>
              <Card className="border-0 shadow-sm">
                <Card.Body className="py-3">
                  <div className="text-muted" style={{ fontSize: 12 }}>
                    Total Liquidado
                  </div>
                  <div className="fw-bold" style={{ fontSize: 24, lineHeight: 1.1 }}>
                    $ {fmtARS(tot.liquidado)}
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col md={4}>
              <Card className="border-0 shadow-sm">
                <Card.Body className="py-3">
                  <div className="text-muted" style={{ fontSize: 12 }}>
                    Total Pagado
                  </div>
                  <div className="fw-bold" style={{ fontSize: 24, lineHeight: 1.1 }}>
                    $ {fmtARS(tot.pagado)}
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col md={4}>
              <Card className="border-0 shadow-sm">
                <Card.Body className="py-3">
                  <div className="text-muted" style={{ fontSize: 12 }}>
                    Saldo Pendiente
                  </div>
                  <div className="fw-bold" style={{ fontSize: 24, lineHeight: 1.1 }}>
                    $ {fmtARS(tot.pendiente)}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Card className="mt-3 shadow-sm border-0">
            <Card.Body>
              <h5 className="mb-3">Choferes</h5>
              <div className="table-responsive">
                <Table
                  striped
                  bordered
                  hover
                  size="sm"
                  className="mb-0"
                  style={{ tableLayout: "fixed" }}
                >
                  <thead>
                    <tr>
                      <th>Chofer</th>
                      <th style={{ width: 90, textAlign: "center" }}>Viajes</th>
                      <th style={{ width: 170, textAlign: "right" }}>Liquidado</th>
                      <th style={{ width: 170, textAlign: "right" }}>Pagado</th>
                      <th style={{ width: 170, textAlign: "right" }}>Saldo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {choferes.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="text-center">
                          Sin saldos.
                        </td>
                      </tr>
                    ) : (
                      choferes.map((c) => (
                        <tr key={c._id || c.nombre}>
                          <td>{c.nombre}</td>
                          <td style={{ textAlign: "center" }}>{c.viajes}</td>
                          <td style={{ textAlign: "right" }}>$ {fmtARS(c.totalLiquidado)}</td>
                          <td style={{ textAlign: "right" }}>$ {fmtARS(c.totalPagado)}</td>
                          <td style={{ textAlign: "right", fontWeight: 700 }}>
                            $ {fmtARS(c.saldo)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </>
      )}
    </Container>
  );
}